import { useParams } from 'wouter';

import useDocument, { useDocumentPreview } from '../../hooks/useDocument';
import { useDocumentSchema } from '../../hooks/useMicroformats';
import { useDocumentUrlGenerator } from '../../hooks/useUrlGenerator';

import Metadata, { MetadataProps } from './Metadata';
import NotfoundMetadata from './NotfoundMetadata';

interface DocumentMetadataProps extends MetadataProps {
    isPreview?: boolean;
}

function DocumentMetadata({ isPreview = false, ...props }: DocumentMetadataProps) {
    const { slug: slugParam, token: previewToken = null } = useParams<{
        slug?: string;
        token?: string;
    }>();
    const { document: previewLoadedDocument = null, notFound: previewNotFound = false } =
        useDocumentPreview(previewToken, {
            keepPreviousData: false,
            enabled: previewToken !== null,
        });
    const { document: normalLoadedDocument = null, notFound: normalNotFound = false } = useDocument(
        slugParam,
        {
            keepPreviousData: false,
            enabled: previewToken === null,
        },
    );
    const document = previewToken !== null ? previewLoadedDocument : normalLoadedDocument;
    const notFound = previewToken !== null ? previewNotFound : normalNotFound;
    const url = useDocumentUrlGenerator();
    const documentSchema = useDocumentSchema(document);
    if (notFound) {
        return <NotfoundMetadata />;
    }
    if (document === null) {
        return null;
    }
    const { type, title, slug, metadata, image, images } = document;
    const { share: shareImage = null } = images || {};
    const { description, brands, keywords, collections } = metadata || {};
    const [brand] = brands || [];
    const { slug: brandSlug } = brand || {};
    const serie = (collections || []).reduce((acc, coll) => {
        const { serie = null, type: collectionType = null } = coll || {};
        if (collectionType.indexOf('serie') !== -1) {
            return coll || acc;
        }
        return serie || acc;
    }, null);
    const { title: serieTitle = null } = serie || {};

    let titleSuffix = null;
    if (type === 'micromag') {
        titleSuffix = ' | Micromag URBANIA';
    }

    return (
        <Metadata
            brand={brand}
            title={
                serieTitle !== null
                    ? `${isPreview ? `[APERÇU] - ` : ''}${title} | ${serieTitle}`
                    : `${isPreview ? `[APERÇU] - ` : ''}${title}`
            }
            titleSuffix={titleSuffix}
            description={description}
            keywords={keywords}
            image={shareImage || image}
            url={
                slug !== null
                    ? url({
                          type,
                          slug,
                      })
                    : null
            }
            rssUrl={`/${brandSlug || 'articles'}.rss`}
            atomUrl={`/${brandSlug || 'articles'}.atom`}
            microformats={documentSchema !== null ? [documentSchema] : []}
            pageType={type}
            document={document}
            {...props}
        />
    );
}

export default DocumentMetadata;
